import type { ReactNode } from 'react'

import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { cx } from '@/Ship/Helpers/cssClass'

import { AssignmentFormFieldCell } from '../Components/AssignmentFormFieldCell'
import { NumberFormFieldCell } from '../Components/NumberFormFieldCell'
import { TaskDelaysCell } from '../Components/TaskDelaysCell'
import { TASK_STATUS_MAP } from '../Contracts/TaskStatus'
import { statusOption } from '../Helpers/statusOption'
import { getUnitLabel } from '../Helpers/unitLabel'

import styles from './RequestFormTemplate.module.css'

/* eslint-disable no-unused-vars -- false positive: ESLint core rule flags param names in function-type signatures */
export interface TaskColumnContext {
    mode: 'view' | 'edit'
    /** Потреба (одиниця виміру, маршрут за замовчуванням) */
    vn: any
    /** Комірки-інпути доступні (`canEdit` у редагуванні) */
    editable: boolean
    onFieldChange?: ((uuid: string, field: string, value: any) => void) | undefined
    openCard: (task: any) => void
}

export interface TaskColumn {
    key: string
    title: string
    width: number
    render: (record: any) => ReactNode
    /** Поля перевірки «Транспорт розподілено», які підсвічують цю комірку */
    invalidFields?: string[]
}

/** Компактні інпути в комірках таблиці */
const SMALL = { size: 'small' as const }

const text = (value: unknown): ReactNode =>
    value === null || value === undefined || value === '' ? (
        <span className={cx(styles, 'muted')}>—</span>
    ) : (
        String(value)
    )

/**
 * Єдиний опис колонок таблиці завдань для перегляду й редагування; з нього ж будуються
 * картки на телефоні (ті самі `title`/`render`). Колонка «⋮» додається у TaskTable.
 */
export const makeTaskColumns = (ctx: TaskColumnContext): TaskColumn[] => {
    const { vn, editable, onFieldChange, openCard } = ctx
    const inputs = editable && onFieldChange !== undefined
    const change = (uuid: string, field: string) => (value: any) => onFieldChange?.(uuid, field, value)

    return [
        {
            key: 'id',
            title: 'ID',
            width: 52,
            render: (record) =>
                record?.id ? (
                    <button type="button" className={cx(styles, 'idLink')} onClick={() => openCard(record)}>
                        #{record.id}
                    </button>
                ) : (
                    text(null)
                ),
        },
        {
            key: 'driver',
            title: 'Водій / ТЗ',
            width: 170,
            invalidFields: ['driver', 'vehicle'],
            render: (record) => {
                const driver = record?.driver
                const vehicle = record?.vehicle
                const plate = vehicle?.registrationNumber || vehicle?.licensePlate
                if (!inputs) {
                    return (
                        <span className={cx(styles, 'twoLines')}>
                            <span title={driver?.fullName || driver?.title}>
                                {text(driver?.fullName || driver?.title)}
                            </span>
                            <span className={cx(styles, 'sub')} title={plate}>
                                {text(plate)}
                            </span>
                        </span>
                    )
                }
                return (
                    <span className={cx(styles, 'stack')}>
                        <AssignmentFormFieldCell
                            key={`${record.uuid}-driver`}
                            fieldName="driver"
                            fieldDef={{
                                type: 'select',
                                resource: 'Setting.User',
                                inputProps: {
                                    ...SMALL,
                                    placeholder: 'Водій',
                                    filter: { filterRoles: { op: 'like', value: 'ROLE_DRIVER' }, status: 'active' },
                                    responseLabel: (item: any) =>
                                        item?.fullName ||
                                        `${item?.lastName || ''} ${item?.firstName || ''}`.trim() ||
                                        '—',
                                },
                            }}
                            initialValue={driver ? { '@id': `/services/users/${driver.uuid}`, ...driver } : null}
                            initialIri={driver?.uuid ? `/services/users/${driver.uuid}` : null}
                            onSave={change(record.uuid, 'driver')}
                            disabled={false}
                        />
                        <AssignmentFormFieldCell
                            key={`${record.uuid}-vehicle`}
                            fieldName="vehicle"
                            fieldDef={{
                                type: 'select',
                                resource: 'Transport.Vehicle',
                                inputProps: {
                                    ...SMALL,
                                    placeholder: 'ТЗ',
                                    responseLabel: (item: any) =>
                                        item?.registrationNumber || item?.licensePlate || item?.title || '—',
                                },
                            }}
                            initialValue={vehicle ? { '@id': `/services/vehicles/${vehicle.uuid}`, ...vehicle } : null}
                            initialIri={vehicle?.uuid ? `/services/vehicles/${vehicle.uuid}` : null}
                            onSave={change(record.uuid, 'vehicle')}
                            disabled={false}
                        />
                    </span>
                )
            },
        },
        {
            key: 'carrier',
            title: 'Перевізник',
            width: 130,
            invalidFields: ['carrier'],
            render: (record) => {
                const carrier = record?.carrier
                const hired = record?.isHired ? <span className={cx(styles, 'hired')}>найманий</span> : null
                if (!inputs) {
                    return (
                        <span className={cx(styles, 'carrier')}>
                            <span className={cx(styles, 'ellipsis')} title={carrier?.title}>
                                {text(carrier?.title)}
                            </span>
                            {hired}
                        </span>
                    )
                }
                return (
                    <span className={cx(styles, 'stack')}>
                        <AssignmentFormFieldCell
                            key={`${record.uuid}-carrier`}
                            fieldName="carrier"
                            fieldDef={{
                                type: 'select',
                                resource: 'Setting.ListCarrier',
                                inputProps: { ...SMALL, placeholder: 'Перевізник', filter: { isActive: true } },
                            }}
                            initialValue={
                                carrier ? { '@id': `/services/lists/carriers/${carrier.code}`, ...carrier } : null
                            }
                            initialIri={carrier?.code ? `/services/lists/carriers/${carrier.code}` : null}
                            onSave={change(record.uuid, 'carrier')}
                            disabled={false}
                        />
                        {hired}
                    </span>
                )
            },
        },
        {
            key: 'totalWeightTonnes',
            title: `Маса, ${getUnitLabel(vn?.unit)}`,
            width: 80,
            invalidFields: ['totalWeightTonnes'],
            render: (record) =>
                inputs ? (
                    <NumberFormFieldCell
                        key={`${record.uuid}-twt`}
                        fieldName="totalWeightTonnes"
                        initialValue={record?.totalWeightTonnes}
                        onChange={change(record.uuid, 'totalWeightTonnes')}
                        inputProps={{ ...SMALL, min: 0, step: 0.01, precision: 3, style: { width: '100%' } }}
                    />
                ) : (
                    text(record?.totalWeightTonnes)
                ),
        },
        {
            key: 'tripsCount',
            title: 'Ходки',
            width: 60,
            invalidFields: ['tripsCount'],
            render: (record) =>
                inputs ? (
                    <NumberFormFieldCell
                        key={`${record.uuid}-tc`}
                        fieldName="tripsCount"
                        initialValue={record?.tripsCount}
                        onChange={change(record.uuid, 'tripsCount')}
                        inputProps={{ ...SMALL, min: 0, step: 1, precision: 0, style: { width: '100%' } }}
                    />
                ) : (
                    text(record?.tripsCount)
                ),
        },
        {
            key: 'route',
            title: 'Маршрут',
            width: 180,
            invalidFields: ['loadingPoint', 'unloadingPoint'],
            render: (record) => {
                const from = record?.loadingPoint?.title || vn?.loadingPoint?.title
                const to = record?.unloadingPoint?.title || vn?.unloadingPoint?.title
                return (
                    <span className={cx(styles, 'route')}>
                        <span title={from}>
                            <i aria-hidden="true">↑</i>
                            <span className={cx(styles, 'srOnly')}>Навантаження: </span>
                            {text(from)}
                        </span>
                        <span title={to}>
                            <i aria-hidden="true">↓</i>
                            <span className={cx(styles, 'srOnly')}>Розвантаження: </span>
                            {text(to)}
                        </span>
                    </span>
                )
            },
        },
        {
            key: 'status',
            title: 'Статус',
            width: 130,
            render: (record) => (
                <StatusBadge
                    option={statusOption(TASK_STATUS_MAP, record?.status)}
                    fallbackLabel={record?.status || '—'}
                />
            ),
        },
        {
            key: 'delay',
            title: 'Затримка',
            width: 120,
            render: (record) => <TaskDelaysCell delays={record?.delays ?? []} taskUuid={record?.uuid ?? ''} />,
        },
        {
            key: 'dispatcherComment',
            title: 'Коментар',
            width: 100,
            render: (record) => (
                <span className={cx(styles, 'ellipsis')} title={record?.dispatcherComment || undefined}>
                    {text(record?.dispatcherComment)}
                </span>
            ),
        },
        {
            key: 'eWaybillNumber',
            title: 'е-ТТН',
            width: 60,
            render: (record) => text(record?.eWaybillNumber),
        },
    ]
}

/** Сума ширин колонок + колонка меню: `scroll.x` таблиці */
export const taskColumnsWidth = (columns: TaskColumn[], menuWidth: number): number =>
    columns.reduce((sum, column) => sum + column.width, menuWidth)
